import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, AlertCircle } from 'lucide-react';

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for payment details in location state
    if (location.state?.paymentDetails) {
      setPaymentDetails(location.state.paymentDetails);
      setLoading(false);
    } else {
      // If no payment details, redirect to dashboard
      setTimeout(() => {
        handleGoToDashboard();
      }, 2000);
    }
  }, [location]);

  const handleGoToDashboard = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (user.user_type === 'employer') {
      navigate('/employer-dashboard');
    } else if (user.user_type === 'candidate') {
      navigate('/candidate-dashboard');
    } else {
      navigate('/dashboard');
    }
  };

  const handlePrintReceipt = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-lg">Loading payment details...</p>
        </div>
      </div>
    );
  }

  if (!paymentDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center p-5">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-10 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Payment Details Not Found</h2>
          <p className="text-gray-600 mb-6">Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center p-5">
      <div className="w-full max-w-md">
        {/* Logo and Header */}
        <div className="text-center mb-8 text-white">
          <div className="flex items-center justify-center mb-3">
            <div className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center mr-3">
              <div className="w-7 h-7 bg-white rounded-full"></div>
            </div>
            <div className="text-left">
              <h2 className="text-3xl font-bold leading-tight">Gulf</h2>
              <h2 className="text-3xl font-bold leading-tight">Worker</h2>
            </div>
          </div>
          <p className="text-lg opacity-90">Connecting Talent with Opportunity</p>
        </div>

        {/* Amount Banner */}
        <div className="bg-green-500 p-8 text-center rounded-t-2xl shadow-lg">
          <h1 className="text-white text-5xl font-bold">
            ₹{paymentDetails.amount?.toFixed(2) || '0.00'}
          </h1>
          {paymentDetails.planName && (
            <p className="text-white text-lg mt-2 opacity-90">
              {paymentDetails.planName} Plan
            </p>
          )}
        </div>

        {/* Payment Details Card */}
        <div className="bg-white rounded-b-2xl shadow-2xl">
          <div className="p-10">
            {/* Success Icon and Message */}
            <div className="text-center mb-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-gray-800 mb-2">
                ₹{paymentDetails.amount?.toFixed(2) || '0.00'}
              </h2>
              <p className="text-xl text-gray-500">Paid Successfully</p>
            </div>

            {/* Payment Details */}
            <div className="space-y-4 mt-8">
              <DetailRow 
                label="Payment Id" 
                value={paymentDetails.razorpay_payment_id || paymentDetails.id || 'N/A'} 
              />
              <DetailRow 
                label="Order Id" 
                value={paymentDetails.razorpay_order_id || paymentDetails.order_id || 'N/A'} 
              />
              <DetailRow 
                label="Method" 
                value={paymentDetails.method || 'UPI'} 
              />
              <DetailRow 
                label="Paid On" 
                value={paymentDetails.created_at 
                  ? new Date(paymentDetails.created_at * 1000).toLocaleString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: true,
                      timeZone: 'Asia/Kolkata'
                    })
                  : new Date().toLocaleString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: true,
                      timeZone: 'Asia/Kolkata'
                    })
                }
              />
              {paymentDetails.email && (
                <DetailRow 
                  label="Email" 
                  value={paymentDetails.email} 
                  valueClass="text-blue-600" 
                />
              )}
              {paymentDetails.mobile && (
                <DetailRow label="Mobile Number" value={paymentDetails.mobile} />
              )}
            </div>

            {/* Support Banner */}
            <div className="bg-green-500 p-5 rounded-lg mt-8 text-center">
              <p className="text-gray-900 font-medium text-sm leading-relaxed">
                For any order related queries please reach out to Gulf Worker at{' '}
                <strong>info@gulfworker.net</strong> or <strong>+91 9321131120</strong>
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 mt-8">
              <button 
                onClick={handleGoToDashboard}
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-3 rounded-lg font-semibold transition-colors"
              >
                Go to Dashboard
              </button>
              <button 
                onClick={handlePrintReceipt}
                className="flex-1 bg-white hover:bg-blue-500 hover:text-white text-blue-500 border-2 border-blue-500 py-3 rounded-lg font-semibold transition-colors"
              >
                Print Receipt
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .min-h-screen > div {
            visibility: visible;
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          button {
            display: none !important;
          }
          .bg-gradient-to-br {
            background: white !important;
          }
        }
      `}</style>
    </div>
  );
};

const DetailRow = ({ label, value, valueClass = 'text-gray-800' }) => (
  <div className="flex justify-between items-center py-4 border-b border-gray-200">
    <span className="text-gray-600 font-medium">{label}</span>
    <span className={`${valueClass} font-semibold text-right max-w-[60%] break-words`}>
      {value}
    </span>
  </div>
);

export default PaymentSuccessPage;